/**
 * DepartmentDashboardPage
 * Main page for department-wise task tracking and performance monitoring
 * Provides comprehensive analytics and comparison across departments
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Skeleton,
  Chip,
  InputLabel,
  Alert,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Users,
  TrendingUp,
  Target,
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart3,
  Activity,
} from 'lucide-react';

import { departmentService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { UserRole, PerformancePeriod } from '@/types';

// Components
import DepartmentComparisonChart from '../components/DepartmentComparisonChart';
import DepartmentTaskBreakdown from '../components/DepartmentTaskBreakdown';
import DepartmentTeamCards from '../components/DepartmentTeamCards';
import DepartmentMemberWorkload from '../components/DepartmentMemberWorkload';
import DepartmentPerformanceMetrics from '../components/DepartmentPerformanceMetrics';
import DepartmentTrendChart from '../components/DepartmentTrendChart';

const DepartmentDashboardPage: React.FC = () => {
  const user = useAppSelector(selectCurrentUser);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [period, setPeriod] = useState<PerformancePeriod>('month');
  const [activeTab, setActiveTab] = useState(0);

  // Check if user has permission to view departments
  const canViewDepartments = user?.role && [
    UserRole.DEPARTMENT_HEAD,
    UserRole.ADMIN,
    UserRole.PROJECT_MANAGER,
    UserRole.MANAGER,
  ].includes(user.role);

  // Fetch all departments
  const { data: departmentsData, isLoading: isLoadingDepartments } = useQuery({
    queryKey: QUERY_KEYS.departments,
    queryFn: () => departmentService.getDepartments(),
    staleTime: 60000,
  });

  const departments = departmentsData ?? [];

  // Auto-select first department or user's department
  React.useEffect(() => {
    if (departments.length > 0 && !selectedDepartment) {
      // For department heads, select their department
      if (user?.role === UserRole.DEPARTMENT_HEAD && user.departmentId) {
        const userDept = departments.find(d => d.id === user.departmentId);
        if (userDept) {
          setSelectedDepartment(userDept.id);
          return;
        }
      }
      // Default to first department
      setSelectedDepartment(departments[0].id);
    }
  }, [departments, selectedDepartment, user]);

  // Fetch department comparison
  const { data: comparisonData, isLoading: isLoadingComparison } = useQuery({
    queryKey: QUERY_KEYS.departmentsComparison(period),
    queryFn: () => departmentService.getDepartmentsComparison(period),
    staleTime: 60000,
  });

  // Fetch selected department performance
  const { data: performanceData, isLoading: isLoadingPerformance } = useQuery({
    queryKey: QUERY_KEYS.departmentPerformance(selectedDepartment, period),
    queryFn: () => departmentService.getDepartmentPerformance(selectedDepartment, period),
    enabled: !!selectedDepartment,
    staleTime: 60000,
  });

  // Fetch task distribution
  const { data: distributionData, isLoading: isLoadingDistribution } = useQuery({
    queryKey: QUERY_KEYS.departmentTaskDistribution(selectedDepartment),
    queryFn: () => departmentService.getDepartmentTaskDistribution(selectedDepartment),
    enabled: !!selectedDepartment,
    staleTime: 60000,
  });

  // Fetch member workload
  const { data: workloadData, isLoading: isLoadingWorkload } = useQuery({
    queryKey: QUERY_KEYS.departmentMemberWorkload(selectedDepartment),
    queryFn: () => departmentService.getDepartmentMemberWorkload(selectedDepartment),
    enabled: !!selectedDepartment,
    staleTime: 60000,
  });

  const comparison = comparisonData?.data;
  const performance = performanceData?.data;
  const distribution = distributionData?.data;
  const workload = workloadData?.data;

  // Period labels
  const periodLabels: Record<PerformancePeriod, string> = {
    day: 'Today',
    week: 'This Week',
    month: 'This Month',
    quarter: 'This Quarter',
    year: 'This Year',
  };

  // Get current department info
  const currentDepartment = departments.find(d => d.id === selectedDepartment);

  if (!canViewDepartments) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to view department analytics. This feature is available for Department Heads, Managers, and Admins.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Building2 size={28} style={{ color: '#3b82f6' }} />
          <Typography variant="h4" fontWeight={700}>
            Department Performance
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Monitor department-wise tasks, track team performance, and analyze productivity metrics
        </Typography>
      </Box>

      {/* Filters Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Department</InputLabel>
          <Select
            value={selectedDepartment}
            label="Department"
            onChange={(e) => setSelectedDepartment(e.target.value)}
          >
            {departments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: dept.color || '#3b82f6',
                    }}
                  />
                  {dept.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 140 }} size="small">
          <InputLabel>Period</InputLabel>
          <Select
            value={period}
            label="Period"
            onChange={(e) => setPeriod(e.target.value as PerformancePeriod)}
          >
            <MenuItem value="day">Today</MenuItem>
            <MenuItem value="week">This Week</MenuItem>
            <MenuItem value="month">This Month</MenuItem>
            <MenuItem value="quarter">This Quarter</MenuItem>
            <MenuItem value="year">This Year</MenuItem>
          </Select>
        </FormControl>

        {currentDepartment && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Chip
              icon={<Users size={14} />}
              label={`${currentDepartment.memberCount} Members`}
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<Activity size={14} />}
              label={`${currentDepartment.activeProjects} Active Projects`}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
      </Box>

      {/* View Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab 
          label="Overview" 
          icon={<BarChart3 size={18} />} 
          iconPosition="start"
          sx={{ textTransform: 'none', fontWeight: 500 }}
        />
        <Tab 
          label="Compare Departments" 
          icon={<TrendingUp size={18} />} 
          iconPosition="start"
          sx={{ textTransform: 'none', fontWeight: 500 }}
        />
        <Tab 
          label="Team Breakdown" 
          icon={<Users size={18} />} 
          iconPosition="start"
          sx={{ textTransform: 'none', fontWeight: 500 }}
        />
        <Tab 
          label="Member Workload" 
          icon={<Target size={18} />} 
          iconPosition="start"
          sx={{ textTransform: 'none', fontWeight: 500 }}
        />
      </Tabs>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Key Metrics */}
          <Grid item xs={12}>
            <DepartmentPerformanceMetrics 
              performance={performance}
              isLoading={isLoadingPerformance}
              period={periodLabels[period]}
            />
          </Grid>

          {/* Task Distribution */}
          <Grid item xs={12} md={6}>
            <DepartmentTaskBreakdown 
              distribution={distribution}
              isLoading={isLoadingDistribution}
            />
          </Grid>

          {/* Performance Trend */}
          <Grid item xs={12} md={6}>
            <DepartmentTrendChart 
              trendData={performance?.trend || []}
              isLoading={isLoadingPerformance}
              period={period}
            />
          </Grid>

          {/* Teams Overview */}
          <Grid item xs={12}>
            <DepartmentTeamCards 
              teams={performance?.teams || []}
              isLoading={isLoadingPerformance}
              departmentName={currentDepartment?.name || ''}
            />
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          {/* Department Comparison Chart */}
          <Grid item xs={12}>
            <DepartmentComparisonChart 
              comparison={comparison}
              isLoading={isLoadingComparison}
              period={periodLabels[period]}
            />
          </Grid>

          {/* Department Rankings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Department Rankings - {periodLabels[period]}
                </Typography>
                {isLoadingComparison ? (
                  <Skeleton variant="rectangular" height={200} />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {comparison?.departments
                      .sort((a, b) => b.completionRate - a.completionRate)
                      .map((dept, index) => (
                        <Box
                          key={dept.departmentId}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 2,
                            bgcolor: selectedDepartment === dept.departmentId ? 'primary.50' : 'grey.50',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: selectedDepartment === dept.departmentId ? 'primary.200' : 'divider',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'grey.100' },
                          }}
                          onClick={() => setSelectedDepartment(dept.departmentId)}
                        >
                          <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{
                              width: 32,
                              color: index < 3 ? 'primary.main' : 'text.secondary',
                            }}
                          >
                            #{index + 1}
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {dept.departmentName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dept.memberCount} members • {dept.projectCount} projects
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" fontWeight={700} color="success.main">
                                {dept.completionRate}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Completion
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" fontWeight={700} color="info.main">
                                {dept.onTimeRate}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                On-Time
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" fontWeight={700} color="warning.main">
                                {dept.utilization}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Utilization
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              icon={
                                dept.riskLevel === 'low' ? <CheckCircle size={14} /> :
                                dept.riskLevel === 'medium' ? <Clock size={14} /> :
                                <AlertTriangle size={14} />
                              }
                              label={dept.riskLevel.charAt(0).toUpperCase() + dept.riskLevel.slice(1)}
                              color={
                                dept.riskLevel === 'low' ? 'success' :
                                dept.riskLevel === 'medium' ? 'warning' : 'error'
                              }
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <DepartmentTeamCards 
              teams={performance?.teams || []}
              isLoading={isLoadingPerformance}
              departmentName={currentDepartment?.name || ''}
              showDetailedView
            />
          </Grid>
        </Grid>
      )}

      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <DepartmentMemberWorkload 
              workload={workload}
              isLoading={isLoadingWorkload}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DepartmentDashboardPage;
